// core/Validation.ts
let _getWidgets = () => [];
/** Установить функцию перечисления виджетов */
function setWidgetEnumerator(fn) {
    _getWidgets = fn;
}
/** Узкий type guard для виджета */
function hasValidate(x) {
    return !!x && typeof x.validate === "function";
}
/**
 * Набор утилит для единообразной валидации элементов и виджетов.
 */
const Validation = {
    events: {
        RUN: "validation:run",
        CHANGED: "validation:changed",
    },
    /** Отметить элемент как ошибочный. */
    mark(el, message) {
        if (el.hasAttribute("title"))
            el.removeAttribute("title");
        if (!el.classList.contains("input-validation-error")) {
            el.classList.add("input-validation-error");
        }
        // Установка сообщения
        if (message && message.trim()) {
            // TODO: Нужно ли подключать tippy?
            el.title = message;
        }
        el.setAttribute("aria-invalid", "true");
        el.dispatchEvent(new CustomEvent(this.events.CHANGED, { bubbles: true, detail: { ok: false, message } }));
    },
    /** Снять ошибку с элемента. */
    clear(el) {
        el.classList.remove("input-validation-error");
        if (el.hasAttribute("title")) {
            // если tippy уже висит — удаляем
            el._tippy?.destroy?.();
            // убираем нативный заголовок, чтобы и делегат не срабатывал
            if (el.hasAttribute("title"))
                el.removeAttribute("title");
        }
        el.setAttribute("aria-invalid", "false");
        el.dispatchEvent(new CustomEvent(this.events.CHANGED, { bubbles: true, detail: { ok: true } }));
    },
    /** Установить итоговое состояние (короткая форма). */
    set(el, ok, message) {
        ok ? this.clear(el) : this.mark(el, message);
        return ok;
    },
    /**
     * Инициализация проверки полей.
     * Принимает один элемент или коллекцию элементов.
     * Игнорируем:
     *  - элементы внутри .im-inputcollection
     *  - .im-numbox (regex для неё не навешиваем — у неё своя логика)
     *
     * Возвращает disposer для снятия всех слушателей.
     */
    init(target) {
        const disposers = [];
        const isIterable = (v) => v && typeof v !== "string" && typeof v[Symbol.iterator] === "function";
        const consider = (el) => {
            this._initValidation(el, disposers);
            if (el.querySelectorAll) {
                el
                    .querySelectorAll("input[data-regex], textarea[data-regex], [data-widget]")
                    .forEach(node => this._initValidation(node, disposers));
            }
        };
        if (isIterable(target)) {
            for (const el of target)
                consider(el);
        }
        else {
            consider(target);
        }
        return () => { disposers.forEach(off => off()); };
    },
    /**
     * Принудительно запускает проверку валидации для уже «проведённых» элементов.
     *
     * @param target Элемент или коллекция элементов, для которых нужно инициировать проверку.
     * @remarks
     * - Срабатывает только для элементов, которые были обработаны в `init()` и отмечены в `_wired`.
     * - Вызов метода инициирует событие `validation:run`, на которое подписан сам валидатор.
     */
    run(target) {
        const isIterable = (v) => v && typeof v !== "string" && typeof v[Symbol.iterator] === "function";
        const trigger = (el) => {
            if (this._wired.has(el)) {
                el.dispatchEvent(new CustomEvent(this.events.RUN, { bubbles: false }));
            }
        };
        if (isIterable(target))
            for (const el of target)
                trigger(el);
        else
            trigger(target);
    },
    /** Не вешать обработчики повторно на один и тот же элемент */
    _wired: new WeakSet(),
    /** Пер-элементные диспозеры для снятия обработчиков (хранятся слабо) */
    _disposers: new WeakMap(),
    /**
 * Снимает обработчики, навешанные Validation для target, и убирает элемент из учёта.
 * Можно вызывать из Widget._onDestroy().
 *
 * @param target HTMLElement или коллекция элементов
 */
    unwire(target) {
        if (!target)
            return;
        const doOne = (el) => {
            const arr = this._disposers.get(el);
            if (arr && arr.length) {
                for (const off of arr) {
                    try {
                        off();
                    }
                    catch { /* ignore */ }
                }
            }
            this._disposers.delete(el);
            this._wired.delete(el);
        };
        doOne(target);
    },
    /**
     * Подключение валидации к конкретному элементу, если это применимо.
     * Комбинирует валидаторы: regex (для input/textarea[data-regex]) и валидаторы виджетов.
     */
    _initValidation(el, disposers) {
        if (this._wired.has(el))
            return;
        const validators = [];
        // 1) REGEX-валидатор
        const isTextInput = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
        if (isTextInput) {
            const input = el;
            if (!input.closest(".im-inputcollection") && !input.classList.contains("im-numbox")) {
                const pattern = input.dataset.regex;
                if (pattern) {
                    try {
                        const re = new RegExp(pattern);
                        validators.push(() => {
                            const ok = re.test(input.value);
                            return ok ? { ok } : { ok, message: input.dataset.error };
                        });
                    }
                    catch {
                        console.error("Ошибка шаблона regex", pattern);
                    }
                }
            }
        }
        // 2) Виджеты
        for (const w of _getWidgets(el)) {
            if (hasValidate(w)) {
                validators.push(() => {
                    try {
                        const res = w.validate() ?? { ok: true };
                        return { ok: !!res.ok, message: res.message };
                    }
                    catch (e) {
                        // Если ошибка, не препятствуем работае
                        console.error('Сбой валидации', e);
                        return { ok: true };
                    }
                });
            }
        }
        if (validators.length === 0)
            return;
        this._wired.add(el);
        const handler = () => {
            let finalOk = true;
            let message;
            for (const run of validators) {
                const res = run();
                if (!res.ok) {
                    finalOk = false;
                    if (!message && res.message && res.message.trim())
                        message = res.message;
                }
            }
            if (!finalOk && !message && isTextInput) {
                message = el.dataset.error;
            }
            this.set(el, finalOk, message);
        };
        // ⬇️ Ведём локальный список для конкретного элемента
        const perElDisposers = [];
        el.addEventListener("input", handler);
        el.addEventListener("change", handler);
        el.addEventListener(this.events.RUN, handler);
        // записываем и в общий массив, и в пер-элементный
        const pushBoth = (off) => { disposers.push(off); perElDisposers.push(off); };
        // сохранить в WeakMap для последующего unwire()
        this._disposers.set(el, perElDisposers);
        pushBoth(() => el.removeEventListener("input", handler));
        pushBoth(() => el.removeEventListener("change", handler));
        pushBoth(() => el.removeEventListener(this.events.RUN, handler));
        if (isTextInput) {
            if (el.value !== "")
                handler();
        }
        else {
            handler();
        }
    }
};

export { Validation, setWidgetEnumerator };
